import { Button } from '@shared/components/ui/button';
import { Search } from '@shared/components/ui/Search/Search';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpArrowDown, faCheck } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';

interface ProductToLocate {
  id: string;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location?: string;
  image?: string;
}

interface ProductsToLocateHeaderProps {
  products: ProductToLocate[];
  currentSort: string;
  onSort: (sortBy: string) => void;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
}

export const ProductsToLocateHeader = ({
  products,
  currentSort,
  onSort,
  searchQuery = '',
  onSearchChange,
}: ProductsToLocateHeaderProps) => {
  const { t } = useTranslation('receiveModule');

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <h2 className="text-title text-lg">{t('ui.products_to_locate')}</h2>
        <span className="inline-flex items-center justify-center rounded-full px-2 bg-bg-info text-info text-sm">
          {products.length}
        </span>
        <span className="inline-flex items-center justify-center rounded-full px-3 bg-card-border text-complementary-text text-xs h-6 light:bg-badge-bg-status-3 light:text-badge-status-3">
          {products.reduce((acc, product) => acc + product.quantity, 0)} {t('ui.units')}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Search
          dataTestId="to-locate-search"
          placeholder={t('items.search_placeholder')}
          value={searchQuery}
          onChange={(e) => onSearchChange?.(e.currentTarget.value)}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8" data-testid="to-locate-sort-button">
              <FontAwesomeIcon icon={faArrowUpArrowDown} className="w-3.5 h-3.5 text-icons" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuItem
              data-testid="to-locate-sort-more-qty"
              onClick={() => onSort('more_qty')}
              className={currentSort === 'more_qty' ? 'bg-card-border' : ''}
            >
              {t('ui.sort.more_qty')}
              {currentSort === 'more_qty' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="to-locate-sort-less-qty"
              onClick={() => onSort('less_qty')}
              className={currentSort === 'less_qty' ? 'bg-card-border' : ''}
            >
              {t('ui.sort.less_qty')}
              {currentSort === 'less_qty' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="to-locate-sort-name-az"
              onClick={() => onSort('name_az')}
              className={currentSort === 'name_az' ? 'bg-card-border' : ''}
            >
              {t('ui.sort.name_az')}
              {currentSort === 'name_az' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="to-locate-sort-name-za"
              onClick={() => onSort('name_za')}
              className={currentSort === 'name_za' ? 'bg-card-border' : ''}
            >
              {t('ui.sort.name_za')}
              {currentSort === 'name_za' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
